<template>
  <a
    v-if="
      content.type === 'text' &&
      getLinkMark(content) &&
      getLinkMark(content)?.attrs &&
      isInternal(getLinkMark(content)?.attrs.href || '')
    "
    href="#"
    :style="getLinkStyle(getLinkMark(content)?.attrs.color)"
    @click.prevent="internalLinkClick(getLinkMark(content)?.attrs.href.substring(2) || '')()"
    v-html="renderTextWithoutLink(content)"
  />
  <a
    v-else-if="content.type === 'text' && getLinkMark(content)"
    v-bind="getLinkAttributes(getLinkMark(content)?.attrs.href || '')"
    :style="getLinkStyle(getLinkMark(content)?.attrs.color)"
    v-html="renderTextWithoutLink(content)"
  />
  <span v-else-if="content.type === 'text'" v-html="renderText(content)" />
  <!-- Only the "Force" presets (isForcedOrderFormat) need this: their output
       mixes digits with a locale-specific word (Hebrew unit/month names), and
       under an RTL `dir` ancestor the browser's own bidi algorithm reorders
       that plain text regardless of the field order these formatters
       constructed — isolated in a forced-LTR `<bdi>` so our order wins. Plain
       (non-Force) presets stay unwrapped, fully trusting the browser. -->
  <span v-else-if="content.type === 'in'" :style="getInterpolationMarkStyles(content)"
    ><CurrencyFormat
      v-if="content.attrs.format === 'currency'"
      variant="code"
      :amount="Number(resolveInterpolation(content.attrs.is))"
    /><CurrencyFormat
      v-else-if="content.attrs.format === 'currency_narrow'"
      :amount="Number(resolveInterpolation(content.attrs.is))"
      variant="narrow"
    /><CurrencyFormat
      v-else-if="content.attrs.format === 'currency_narrow_trim'"
      :amount="Number(resolveInterpolation(content.attrs.is))"
      variant="narrow-trim"
    /><bdi v-else-if="isForcedOrderFormat(content.attrs.format)" dir="ltr">{{
      formatInterpolation(content)
    }}</bdi
    ><span v-else>{{ formatInterpolation(content) }}</span></span
  >
  <span
    v-else-if="content.type === 'el'"
    :class="[
      'el',
      content.attrs?.type ? `el-${content.attrs.type}` : null,
      content.attrs?.variant ? `el-variant-${content.attrs.variant}` : null,
    ]"
  >
    <TipTapRenderer
      v-for="(child, i) in nowrapRenderGroups(content.content)"
      :key="i"
      :content="child"
    />
  </span>
  <!-- One wrapper for the whole bound run — see nowrapRenderGroups. No
       whitespace inside the tags: it would render as text between the run's
       parts. -->
  <span v-else-if="content.type === NOWRAP_GROUP_TYPE" style="white-space: nowrap"
    ><TipTapRenderer v-for="(child, i) in content.content" :key="i" :content="child"
  /></span>
  <component
    v-else-if="content.type === 'heading'"
    :is="`h${content.attrs.level}`"
    class="font-bold my-2"
    :style="getNodeStyle(content)"
  >
    <TipTapRenderer
      v-for="(child, i) in nowrapRenderGroups(content.content)"
      :key="i"
      :content="child"
    />
  </component>
  <template v-else-if="content.type === 'doc'">
    <TipTapRenderer
      v-for="(child, i) in nowrapRenderGroups(content.content)"
      :key="i"
      :content="child"
    />
  </template>
  <p v-else-if="content.type === 'paragraph'" :style="getNodeStyle(content)">
    <TipTapRenderer
      v-for="(child, i) in nowrapRenderGroups(content.content)"
      :key="i"
      :content="child"
    />
  </p>
  <br v-else-if="content.type === 'hardBreak'" />
  <hr v-else-if="content.type === 'horizontalRule'" />
  <component
    v-else-if="nodeMap[content.type as keyof typeof nodeMap]"
    :is="nodeMap[content.type as keyof typeof nodeMap]"
  >
    <TipTapRenderer
      v-for="(child, i) in nowrapRenderGroups(content.content)"
      :key="i"
      :content="child"
    />
  </component>

  <template v-else>
    <TipTapRenderer
      v-for="(child, i) in nowrapRenderGroups(content.content)"
      :key="i"
      :content="child"
    />
  </template>
</template>
<script setup lang="ts">
import TipTapRenderer from './TipTapRenderer.vue'
import { usePipe } from '../composables/usePipe' // Adjust path to match your structure
import { useComponentScopedVariablesIfAvailable } from '@render/composables/useComponentScopedVariables'
import CurrencyFormat from '@render/components/generic/CurrencyFormat.vue'
import {
  formatHeightWeight,
  isHeightWeightFormat,
  formatDuration,
  isDurationFormat,
  formatDate,
  isDateFormat,
  formatPeriod,
  isPeriodFormat,
  formatPeriodValue,
  isPeriodValueFormat,
  formatTextCase,
  isTextCaseFormat,
  isForcedOrderFormat,
} from '@render/lib/interpolationFormat'
import { formatNumber } from '@render/lib/localeConfig'
import { useLocale } from '@render/composables/useLocale'
import { tiptapToText } from '@render/lib/tiptapToText'
import { toLogicalTextAlign } from '@render/lib/textAlign'
import './elements.css'

defineProps({
  content: {
    type: Object,
    default: () => ({ type: 'doc', content: [] }),
  },
  mode: {
    type: String as () => 'doc' | 'single' | 'plain',
    default: 'doc',
  },
})

// --- Store Integration ---
const pipe = usePipe()
// Component-scoped variables (comp.*) — provided by render components via
// useComponentScopedVariables. Without this branch the built pipe falls back
// to pipe.getData('comp.…'), which always returns undefined, so animated
// scoped values (e.g. comp.animatedWeight on GoalLineChart) appear static
// in production while working correctly in the editor.
const scopedVars = useComponentScopedVariablesIfAvailable()

// Helper to resolve variable values from the Pipe store / scoped getter.
const resolveInterpolation = (key: string) => {
  if (!key) return '??'
  if (key.startsWith('comp.')) {
    if (!scopedVars) return ''
    const values = scopedVars.getValues()
    const v = values?.[key.slice(5)]
    return v ?? ''
  }
  const val = pipe.getData(key)
  return val !== undefined ? val : ''
}

const { config, localeString: locale, measurementSystem } = useLocale()

const resolveTranslationText = (key: string): string | undefined => {
  const data = pipe.getData(key)
  return data ? tiptapToText(data as object) : undefined
}

// Single string-returning dispatcher for non-currency interpolation arms.
// Collapsing the per-format arms into one branch (rendered as a single
// <span>{{ formatInterpolation(content) }}</span>) ensures the rendered DOM
// has the same shape on the SSR and client passes — previously each
// <template v-else-if>{{...}}</template> arm produced a Fragment whose text
// vnode SSR collapsed to nothing for empty values, causing hydration
// mismatches (`<!--]-->` vs `Symbol(v-txt)`).
//
// `format === 'number_trim'`: round to N decimals AND drop trailing zeros
// (1234.5 / decimals=2 → "1234.5", 1234 / decimals=1 → "1234"). Otherwise
// returns the toFixed STRING so trailing zeros are preserved (1234.5 with
// decimals=2 → "1234.50", not "1234.5").
const formatInterpolation = (node: Record<string, unknown>): string => {
  const attrs = (node.attrs ?? {}) as {
    is?: string
    format?: string | null
    decimals?: number | null
    periodUnitVar?: string | null
    periodUnit?: string | null
    periodHideOne?: boolean | null
  }
  const value = resolveInterpolation(attrs.is ?? '')
  const format = attrs.format
  if (isHeightWeightFormat(format)) {
    return String(
      formatHeightWeight(value, format, measurementSystem.value, config.value, attrs.decimals) ??
        '',
    )
  }
  if (isDurationFormat(format)) {
    return String(formatDuration(value, format) ?? '')
  }
  if (isDateFormat(format)) {
    return String(formatDate(value, format, locale.value) ?? '')
  }
  if (isPeriodValueFormat(format)) {
    return String(
      formatPeriodValue(value, {
        unitValue: attrs.periodUnitVar ? resolveInterpolation(attrs.periodUnitVar) : undefined,
        manualUnit: attrs.periodUnit,
        hideOne: attrs.periodHideOne,
        resolveTranslation: resolveTranslationText,
        formatCount: (count) => formatNumber(count, config.value, attrs.decimals ?? null),
        period: config.value.period,
      }) ?? '',
    )
  }
  if (isPeriodFormat(format)) {
    return String(formatPeriod(value, format, resolveTranslationText) ?? '')
  }
  if (isTextCaseFormat(format)) {
    return String(formatTextCase(value, format, locale.value) ?? '')
  }
  if (value === null || value === undefined || value === '') return ''
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)
  const decimals = attrs.decimals
  if (decimals === null || decimals === undefined) return String(num)
  if (format === 'number_trim') return String(Number(num.toFixed(decimals)))
  return num.toFixed(decimals)
}

// --- Link Handling ---

// Helper: Find the link mark in the node's marks array
const getLinkMark = (
  node: Record<string, unknown>,
): { attrs: { href: string; color?: string | null } } | undefined => {
  return (node.marks as Array<Record<string, unknown>>)?.find((m) => m.type === 'link') as
    | { attrs: { href: string; color?: string | null } }
    | undefined
}

const getLinkStyle = (color: string | null | undefined): Record<string, string> | undefined => {
  return color ? { color } : undefined
}

// Helper: Check if href is our internal format (#+pageId)
const isInternal = (href: string) => {
  return href && href.startsWith('#+')
}

// Helper: Check if href references a constant (#const.<scope>.<name>) rather
// than carrying a literal URL. Mirrors `isInternal`'s `#+` convention, so the
// href stays a plain string attribute on the stock `link` mark — no schema
// change on either the editor or API TipTap extensions.
const isConstantRef = (href: string) => {
  return href && href.startsWith('#const.')
}

const internalLinkClick = (pageId: string) => () => {
  pipe.navigateToPageId(pageId)
}

const getLinkAttributes = (href: string) => {
  if (isInternal(href)) {
    // Internal: Convert ID to Path
    const pageId = href.substring(2) // Remove '#+'
    const page = pipe.getPageById(pageId)

    // Fallback: If page not found, go to home or stay
    const path = page ? `/${page.path}` : '/'

    // Clean up double slashes just in case
    return { to: path.replace(/\/{2,}/g, '/') }
  } else if (isConstantRef(href)) {
    // `href.slice(1)` turns `#const.<key>` into `const.<key>` — exactly the
    // prefixed key `pipe.getData` resolves.
    const resolved = pipe.getData(href.slice(1))
    return {
      href: typeof resolved === 'string' ? resolved : '',
      target: '_blank',
      rel: 'noopener noreferrer',
    }
  } else {
    // External: Standard HTML attributes
    return {
      href,
      target: '_blank',
      rel: 'noopener noreferrer',
    }
  }
}

// --- The `nowrap` mark ("keep on one line") ---
// Editor half: NowrapExtension in components/TipTap. Marks are rendered here
// rather than there, so both halves have to agree on the emitted CSS — same
// mirroring rule as the stylesheet value renderers.

/** Synthetic node type standing in for one bound run. Prefixed so it can never collide with a real ProseMirror node type. */
const NOWRAP_GROUP_TYPE = '__nowrapGroup'

const hasNowrapMark = (node: unknown): boolean => {
  const marks = (node as { marks?: Array<{ type?: string }> } | null)?.marks
  return Array.isArray(marks) && marks.some((m) => m?.type === 'nowrap')
}

const nowrapRenderGroups = (content: unknown): Array<Record<string, unknown>> => {
  const children = Array.isArray(content) ? (content as Array<Record<string, unknown>>) : []
  // Fast path — and, more importantly, an identical DOM shape to before this
  // feature for every document that doesn't use the mark.
  if (!children.some(hasNowrapMark)) return children

  const grouped: Array<Record<string, unknown>> = []
  for (const child of children) {
    if (!hasNowrapMark(child)) {
      grouped.push(child)
      continue
    }
    const previous = grouped[grouped.length - 1]
    if (previous?.type === NOWRAP_GROUP_TYPE) {
      ;(previous.content as Array<Record<string, unknown>>).push(child)
    } else {
      grouped.push({ type: NOWRAP_GROUP_TYPE, content: [child] })
    }
  }
  return grouped
}

// --- Helpers ---
const getNodeStyle = (node: Record<string, unknown>): Record<string, string> | undefined => {
  const attrs = node.attrs as Record<string, unknown> | undefined
  // Logical alignment so left/right mirror under RTL; 'start' is the natural
  // flow default, so it needs no explicit style (matches the old 'left' skip).
  const align = toLogicalTextAlign(attrs?.textAlign as string | undefined)
  if (!align || align === 'start') return undefined
  return { textAlign: align }
}

// --- Mappings ---
const nodeMap = {
  bulletList: 'ul',
  orderedList: 'ol',
  listItem: 'li',
  blockquote: 'blockquote',
  codeBlock: 'pre',
  table: 'table',
  tableRow: 'tr',
  tableHeader: 'th',
  tableCell: 'td',
}

const markMap = {
  bold: 'strong',
  italic: 'em',
  strike: 's',
  underline: 'u',
  code: 'code',
  link: 'a',
}

// const getAttrs = (type, attrs) => {
//   if (type === 'link') {
//     // Ensure we keep the special href for our click handler to read
//     return {
//       href: attrs.href,
//       target: attrs.target || (attrs.href.startsWith('#+') ? '_self' : '_blank'),
//       rel: 'noopener',
//     }
//   }
//   if (type === 'image') return { src: attrs.src, alt: attrs.alt, title: attrs.title }
//   return {}
// }

const renderText = (node: Record<string, unknown>) => {
  if (!node.marks) return node.text
  return applyMarks(node.text as string, node.marks as Array<Record<string, unknown>>)
}

const renderTextWithoutLink = (node: Record<string, unknown>) => {
  if (!node.marks) return node.text
  const marksWithoutLink = (node.marks as Array<Record<string, unknown>>).filter(
    (m) => m.type !== 'link',
  ) as Array<Record<string, unknown>>
  return applyMarks(node.text as string, marksWithoutLink)
}

const getInterpolationMarkStyles = (
  node: Record<string, unknown>,
): Record<string, string> | undefined => {
  const marks = node.marks as
    | Array<{ type: string; attrs?: Record<string, string | null | undefined> }>
    | undefined
  if (!marks || marks.length === 0) return undefined
  const styles: Record<string, string> = {}
  const decorations: string[] = []
  for (const mark of marks) {
    if (mark.type === 'bold') styles.fontWeight = 'bold'
    else if (mark.type === 'italic') styles.fontStyle = 'italic'
    else if (mark.type === 'strike') decorations.push('line-through')
    else if (mark.type === 'underline') decorations.push('underline')
    else if (mark.type === 'textStyle') {
      if (mark.attrs?.color) styles.color = mark.attrs.color
      if (mark.attrs?.fontSize) styles.fontSize = mark.attrs.fontSize
      if (mark.attrs?.fontFamily) styles.fontFamily = mark.attrs.fontFamily
      if (mark.attrs?.fontWeight) styles.fontWeight = mark.attrs.fontWeight
    }
  }
  if (decorations.length) styles.textDecoration = decorations.join(' ')
  return Object.keys(styles).length ? styles : undefined
}

const applyMarks = (text: string, marks: Array<Record<string, unknown>>) => {
  // Nest the textStyle (color / font-size / font-family) span OUTSIDE decoration
  // tags like <u> / <s> so the decoration line inherits the text color via
  // currentColor — matching how the TipTap editor renders the color span
  // wrapping <u>. The reduce below makes the last-processed mark the outermost
  // wrapper, so textStyle must come last. Without this, <u> wraps the color span
  // and the underline paints in the inherited (default) color, not the text color.
  const orderedMarks = [...marks].sort(
    (a, b) => (a.type === 'textStyle' ? 1 : 0) - (b.type === 'textStyle' ? 1 : 0),
  )
  return orderedMarks.reduce((acc, mark) => {
    // 1. Handle textStyle (color + fontSize live on the same mark)
    if (mark.type === 'textStyle') {
      const attrs = mark.attrs as Record<string, string | null | undefined> | undefined
      const styles: string[] = []
      if (attrs?.color) styles.push(`color: ${attrs.color}`)
      if (attrs?.fontSize) styles.push(`font-size: ${attrs.fontSize}`)
      if (attrs?.fontFamily) styles.push(`font-family: ${attrs.fontFamily}`)
      if (attrs?.fontWeight) styles.push(`font-weight: ${attrs.fontWeight}`)
      if (styles.length === 0) return acc
      return `<span style="${styles.join('; ')}">${acc}</span>`
    }

    // 2. Handle Standard Tags (Bold, Italic, etc.)
    // @ts-expect-error - We know mark.type is a string, but TS can't infer it from the object
    const tag = markMap[mark.type]

    if (!tag) return acc // Skip unknown marks
    return `<${tag}>${acc}</${tag}>`
  }, text)
}
</script>

<style scoped>
/* Optional: Basic styling for Tiptap content */
.tiptap-text :deep(a) {
  color: #2563eb;
  text-decoration: underline;
  cursor: pointer;
}
.tiptap-text :deep(a:hover) {
  color: #1d4ed8;
}
</style>
